import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppProviders } from '@/app/providers';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { HomeRoute } from '@/components/auth/HomeRoute';
import { PublicOnlyRoute } from '@/components/auth/PublicOnlyRoute';
import { StaffRoute } from '@/components/auth/StaffRoute';
import { AppShell } from '@/components/layout/AppShell';
import { AdminCatalogPage } from '@/modules/admin/AdminCatalogPage';
import { AdminOverviewPage } from '@/modules/admin/AdminOverviewPage';
import { BillingPage } from '@/modules/billing/BillingPage';
import { CheckoutPaymentPage } from '@/modules/billing/CheckoutPaymentPage';
import { CheckoutSuccessPage } from '@/modules/billing/CheckoutSuccessPage';
import { ServerDetailPage } from '@/modules/servers/ServerDetailPage';
import { ServersPage } from '@/modules/servers/ServersPage';
import { AdminApiTokensPage } from '@/modules/admin/AdminApiTokensPage';
import { AdminCustomersPage } from '@/modules/admin/AdminCustomersPage';
import { AdminPlatformBackupsPage } from '@/modules/admin/AdminPlatformBackupsPage';
import { AdminSupportPage } from '@/modules/admin/AdminSupportPage';
import { SupportPage } from '@/modules/support/SupportPage';
import { TicketDetailPage } from '@/modules/support/TicketDetailPage';
import { BackupsPage } from '@/modules/backups/BackupsPage';
import { MonitoringPage } from '@/modules/monitoring/MonitoringPage';
import { OutagePage } from '@/modules/monitoring/OutagePage';
import { CommandRunnerPage } from '@/modules/automation/CommandRunnerPage';
import { PlaybookRunPage } from '@/modules/automation/PlaybookRunPage';
import { PlaybooksPage } from '@/modules/automation/PlaybooksPage';
import { RunDetailPage } from '@/modules/automation/RunDetailPage';
import { RunsPage } from '@/modules/automation/RunsPage';
import { TemplateRunPage } from '@/modules/automation/TemplateRunPage';
import { TemplatesPage } from '@/modules/automation/TemplatesPage';
import { AdminSubscriptionsPage } from '@/modules/admin/AdminSubscriptionsPage';
import { ForgotPasswordPage } from '@/modules/auth/ForgotPasswordPage';
import { GoogleCallbackPage } from '@/modules/auth/GoogleCallbackPage';
import { GOOGLE_REDIRECT_PATH } from '@/modules/auth/google-oauth';
import { LoginPage } from '@/modules/auth/LoginPage';
import { RegisterPage } from '@/modules/auth/RegisterPage';
import { ResetPasswordPage } from '@/modules/auth/ResetPasswordPage';
import { VerifyEmailPage } from '@/modules/auth/VerifyEmailPage';
import { DashboardRoute } from '@/components/auth/DashboardRoute';
import { ApiTokensPage } from '@/modules/settings/ApiTokensPage';
import { SecurityPage } from '@/modules/settings/SecurityPage';
import { SessionsPage } from '@/modules/settings/SessionsPage';
import { TeamPage } from '@/modules/settings/TeamPage';
import { SettingsLayout } from '@/modules/settings/SettingsLayout';
import { NotFoundPage } from '@/modules/system/NotFoundPage';
import { SystemStatusPage } from '@/modules/system/SystemStatusPage';
import { ArticlePage } from '@/modules/marketing/ArticlePage';
import { BlogIndexPage } from '@/modules/marketing/blog/BlogIndexPage';
import { BlogPostPage } from '@/modules/marketing/blog/BlogPostPage';
import { ContactPage } from '@/modules/marketing/ContactPage';
import { DocsPage } from '@/modules/marketing/DocsPage';
import { HelpDocPage, HelpGuidePage, HelpHomePage } from '@/modules/docs/HelpPages';
import { PublicDocPage, PublicReferenceIndexPage } from '@/modules/docs/PublicDocPages';
import { PublicJuni } from '@/modules/juni/PublicJuni';
import { GuidePage } from '@/modules/marketing/GuidePage';
import { PrivacyPage, TermsPage } from '@/modules/marketing/LegalPages';
import { PricingPage } from '@/modules/marketing/PricingPage';
import {
  FeatureArticlesPage,
  MonitoringGuidesPage,
} from '@/modules/marketing/ArticleIndexPage';

export function App() {
  return (
    <AppProviders>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Email verification is reachable whether or not you are signed in. */}
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* The public front door. Signed-in visitors are moved on to /dashboard. */}
          <Route path="/" element={<HomeRoute />} />
          {/* ## Two sections of the landing page that had to become pages
              `#pricing` and `#features` are not addresses. Neither can be a search result, given
              its own description, or linked from an invoice or a support reply — and "pricing" is
              among the first things anybody searches about a subscription product. They are still
              sections of the home page as well; the page and the section render the same
              components, so there is one place each fact is written. */}
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/features" element={<FeatureArticlesPage />} />
          {/* Reached from the footer. Each is a real page rather than a link to nowhere —
              a footer column of dead links is worse than a shorter footer. */}
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/docs/playbooks" element={<PublicReferenceIndexPage kind="playbook" />} />
          <Route path="/docs/templates" element={<PublicReferenceIndexPage kind="template" />} />
          <Route path="/docs/troubleshooting" element={<PublicDocPage kind="troubleshooting" />} />
          <Route path="/docs/playbooks/:key" element={<PublicDocPage kind="playbook" />} />
          <Route path="/docs/templates/:key" element={<PublicDocPage kind="template" />} />
          <Route path="/docs/:slug" element={<GuidePage />} />
          {/* The articles index used to live here. Moved to /features, which is the word
              people search and link with; this keeps the old address working rather than
              answering it with the login form. */}
          <Route path="/articles" element={<Navigate to="/features" replace />} />
          <Route path="/guides" element={<MonitoringGuidesPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* The long-form articles, kept off the landing page so it stays scannable. */}
          {/* One page per feature. A single route renders them all from the slug. */}
          <Route path="/features/:slug" element={<ArticlePage />} />
          <Route path="/monitoring/:slug" element={<ArticlePage />} />

          {/* Signed-out only */}
          <Route element={<PublicOnlyRoute />}>
            <Route path="/login" element={<LoginPage />} />
            {/* Declared before the protected `admin` section and more specific than it, so the
                router matches this rather than treating "login" as an admin sub-page. */}
            <Route path="/admin/login" element={<LoginPage staff />} />
            <Route path="/register" element={<RegisterPage />} />
            {/* Where Google sends the browser back. Public: nobody is signed in yet. */}
            <Route path={GOOGLE_REDIRECT_PATH} element={<GoogleCallbackPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Authenticated application */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppShell />}>
              {/* Customers only — staff are redirected to theirs. See DashboardRoute. */}
              <Route path="dashboard" element={<DashboardRoute />} />
              <Route path="system" element={<SystemStatusPage />} />

              <Route path="servers" element={<ServersPage />} />
              <Route path="servers/:id" element={<ServerDetailPage />} />
              <Route path="billing" element={<BillingPage />} />
              {/* Where the payment gateway returns a customer. BillingConfig::checkoutSuccessUrl()
                  has always pointed here; the route did not exist, so everybody who completed a
                  payment landed on the 404 page — at the exact moment they most need reassurance
                  that their card was charged for something real. */}
              <Route path="billing/checkout/success" element={<CheckoutSuccessPage />} />
              {/* Paddle's "default payment link" points here. Paddle.js reads `_ptxn` from the
                  query string and opens the checkout itself; see CheckoutPaymentPage. */}
              <Route path="billing/pay" element={<CheckoutPaymentPage />} />

              {/* Automation: everything it does is a run, and every run keeps its record. */}
              <Route path="automation" element={<Navigate to="/automation/playbooks" replace />} />
              <Route path="automation/playbooks" element={<PlaybooksPage />} />
              <Route path="automation/playbooks/:key" element={<PlaybookRunPage />} />
              <Route path="automation/templates" element={<TemplatesPage />} />
              <Route path="automation/templates/:key" element={<TemplateRunPage />} />
              <Route path="automation/commands" element={<CommandRunnerPage />} />
              <Route path="automation/runs" element={<RunsPage />} />
              <Route path="automation/runs/:id" element={<RunDetailPage />} />

              <Route path="monitoring" element={<MonitoringPage />} />
              {/* Two segments, so the public /monitoring/:slug articles above cannot match it. */}
              <Route path="monitoring/outages/:id" element={<OutagePage />} />
              <Route path="backups" element={<BackupsPage />} />

              {/* Reachable by everyone, including staff — a staff account's own tickets are a
                  legitimate thing to have, and this endpoint is tenant-scoped for them like
                  anybody else. The queue at /admin/support is the cross-tenant surface. */}
              <Route path="help" element={<HelpHomePage />} />
              <Route path="help/guides/:slug" element={<HelpGuidePage />} />
              <Route path="help/playbooks/:key" element={<HelpDocPage kind="playbook" />} />
              <Route path="help/templates/:key" element={<HelpDocPage kind="template" />} />
              <Route path="help/troubleshooting" element={<HelpDocPage kind="troubleshooting" />} />
              <Route path="support" element={<SupportPage />} />
              <Route path="support/:id" element={<TicketDetailPage />} />

              <Route element={<StaffRoute />}>
                <Route path="admin" element={<AdminOverviewPage />} />
                <Route path="admin/customers" element={<AdminCustomersPage />} />
                <Route path="admin/support" element={<AdminSupportPage />} />
                <Route path="admin/platform-backups" element={<AdminPlatformBackupsPage />} />
                <Route path="admin/subscriptions" element={<AdminSubscriptionsPage />} />
                <Route path="admin/api-tokens" element={<AdminApiTokensPage />} />
                <Route path="admin/catalog" element={<AdminCatalogPage />} />
              </Route>

              <Route path="settings" element={<SettingsLayout />}>
                <Route index element={<Navigate to="/settings/security" replace />} />
                <Route path="security" element={<SecurityPage />} />
                <Route path="sessions" element={<SessionsPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="api-tokens" element={<ApiTokensPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Routes>
        {/* Outside the routes, so a visitor's conversation stays open from page to page. */}
        <PublicJuni />
      </BrowserRouter>
    </AppProviders>
  );
}
